import { useMutation } from "@tanstack/react-query";
import { type ClientInferResponses } from "@ts-rest/core";
import { type apiContract } from "@kanpla/api-contract";

import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { logger } from "~/services/logger";
import { client } from "~/api/client";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";

import { CODE_FORMATS } from "../use-scanner/code-formats";
import { type CodeValidationResult } from "../use-scanner/validate";

export type TicketValidationOrderDetailsResponse = ClientInferResponses<
  typeof apiContract.v1.pos.tills.validateQr,
  200
>["body"]["orderDetails"];

export const useVerifyTicket = () => {
  return useMutation({
    mutationFn: async (scannedCodePayload: CodeValidationResult) => {
      if (scannedCodePayload.format !== CODE_FORMATS.QR_ORDER_ID) {
        logger.warn("Invalid ticket code scanned", { scannedCodePayload });
        throw new Error("Invalid ticket code scanned");
      }

      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const result = await client.v1.pos.tills.validateQr({
        params: {
          tillId: auth.tillId
        },
        body: {
          code: scannedCodePayload.value
        }
      });

      if (result.status === 200) {
        return result.body;
      }

      throw result;
    },
    onError: (error) => {
      logger.error(error, "Ticket validation failed");
    }
  });
};
